<template>
    <Dialog
        class="study-assistant-modal"
        modal
        :visible="props.showModal"
        :header="props.modalInfo.header"
        @update:visible="emit('closeStudyAssistantModal')"
    >
        <div
            class="study-assistant-modal__body"
        >
            <div
                v-if="props.modalInfo.errorMessage"
                class="study-assistant-modal__message study-assistant-modal__error"
            >
                <i
                    class="pi pi-exclamation-circle"
                    aria-hidden="true"
                />
                {{ props.modalInfo.errorMessage }}
            </div>
            <div
                v-if="props.modalInfo.warningMessage"
                class="study-assistant-modal__message study-assistant-modal__warning"
            >
                <i
                    class="pi pi-exclamation-triangle"
                    aria-hidden="true"
                />
                {{ props.modalInfo.warningMessage }}
            </div>
            <div
                v-if="props.modalInfo.isLoading && props.modalInfo.body[modalIndex] === 'Loading'"
                class="study-assistant-modal__loading"
            >
                <img
                    :src="MagicWandPinkIcon"
                    alt="Study Assistant Loading Icon"
                    aria-hidden="true"
                />
                AI is working on it...
            </div>
            <div v-else-if="props.modalInfo.header === 'Define'" v-html="props.modalInfo.body[props.modalIndex]"/>
            <div
                v-else
                class="study-assistant-modal__body__text"
            >
                {{ props.modalInfo.body[props.modalIndex] }}
            </div>
            <div
                v-if="props.modalInfo.body.length > 1"
                class="study-assistant-modal__body__pagination"
            >
                <i
                    class="pi pi-chevron-left"
                    :class="props.modalIndex === 0 ? 'disabled' : ''"
                    aria-label="Previous result"
                    :tabindex="props.modalIndex === 0 ? -1 : 0"
                    role="button"
                    @click="emit('changeStudyAssistantModalIndex', -1)"
                />
                <div>
                    {{ props.modalIndex + 1 }} / {{ props.modalInfo.body.length }}
                </div>
                <i
                    class="pi pi-chevron-right"
                    :class="props.modalIndex === props.modalInfo.body.length - 1 ? 'disabled' : ''"
                    aria-label="Next result"
                    :tabindex="props.modalIndex === props.modalInfo.body.length - 1 ? -1 : 0"
                    role="button"
                    @click="emit('changeStudyAssistantModalIndex', 1)"
                />
            </div>
            <button
                v-if="props.modalInfo.body[props.modalIndex] !== 'Loading'"
                aria-label="Regenerate AI response"
                @click="emit('regenerateAIOutput', props.modalInfo.editor)"
            >
                <i
                    class="pi pi-replay"
                    aria-hidden="true"
                />
                <div>Regenerate</div>
            </button>
        </div>
        <template #footer>
            <button
                v-for="action in props.footerActions"
                :key="action.name"
                class="study-assistant-modal__icon"
                :class="props.modalInfo.body[props.modalIndex] === 'Loading' ? 'disabled' : ''"
                :aria-label="action.name"
                :disabled="props.modalInfo.body[props.modalIndex] === 'Loading'"
                @click="() => {
                    !props.modalInfo.isLoading && action.onclick(props.modalInfo.editor, props.modalInfo.body[modalIndex])
                }"
            >
                <img
                    :src="action.image"
                    :alt="`${action.name} Icon`"
                    aria-hidden="true"
                />
            </button>
        </template>
    </Dialog>
</template>

<script setup lang="ts">
import { ModalInfo, FooterActions } from '@/types/study-assistant-interfaces';

const emit = defineEmits([
    'closeStudyAssistantModal',
    'changeStudyAssistantModalIndex',
    'regenerateAIOutput'
]);

const props = defineProps<{
    showModal: boolean,
    modalInfo: ModalInfo,
    modalIndex: number,
    MagicWandPinkIcon: string,
    footerActions: FooterActions[],
}>();
</script>

<style lang="scss">
.p-dialog.study-assistant-modal {
    width: 33rem;
    min-height: 33rem;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    border: unset;
    border-radius: 2rem;
    background-color: map-get($colours, accent-purple-3);

    .p-dialog-header {
        background-color: map-get($colours, accent-purple-3);
        display: flex;
        flex-direction: row;
        justify-content: center;
        border-top-right-radius: 2rem;
        border-top-left-radius: 2rem;
        height: 15%;

        &-actions {
            position: absolute;
            right: 1.5rem;
            top: 1.5rem;
        }
    }

    .p-dialog-title {
        font-size: 2.4rem;
        font-family: $font-secondary;
        font-weight: bold;
        color: map-get($colours, white);
    }

    .p-dialog-content {
        background-color: map-get($colours, accent-purple-3);
    }

    .study-assistant-modal__body {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: space-around;
        min-height: 23rem;
        font-family: $font-default;
        color: map-get($colours, white);

        button {
            padding: 0.6rem 1.2rem;
            border-radius: 2rem;
            display: flex;
            justify-content: space-between;
            align-items: center;
            gap: 0.8rem;
            background-color: map-get($colours, white);

            &:focus {
                outline: 1px solid map-get($colours, accent-purple-2);
                outline-offset: 1px;
            }
        }

        img {
            width: 5rem;
        }

        &__header {
            font-size: 1.8rem;
            font-weight: 600;
            display: flex;
            align-items: center;
            gap: 0.8rem;
        }

        &__subtext {
            font-size: 1.2rem;
            color: #bababa;
        }

        &__subheader {
            font-size: 1.2rem;
            font-weight: 600;
        }

        &__list {
            font-size: 1.2rem;
            padding-left: 2rem;
        }

        &__text {
            width: 100%;
            min-height: 50%;
            overflow-y: auto;
            padding: 0.8rem 0.4rem;
        }

        &__pagination {
            display: flex;
            justify-content: space-between;
            align-items: center;
            gap: 0.8rem;

            i {
                font-size: 1.25rem;
                cursor: pointer;

                &.disabled {
                    color: #FFFFFF66;
                    cursor: not-allowed;
                }

                &:focus {
                    outline: 1px solid map-get($colours, accent-purple-2);
                    outline-offset: 1px;
                }
            }
        }
    }

    .study-assistant-modal__loading {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: space-around;
        min-height: 70%;
        font-family: $font-default;
        font-size: 2rem;
        color: map-get($colours, white);
    }

    .study-assistant-modal__message {
        padding: 0.8rem;
        border-radius: 0.6rem;
        display: flex;
        align-items: center;
        gap: 0.8rem;
        width: 100%;
    }

    .study-assistant-modal__error {
        background-color: #F5DEE2;
        color: #B00020;
        border-left: 0.8rem solid #B00020;
        i {
            color: #B0002080;
        }
    }

    .study-assistant-modal__warning {
        background-color: #FFF6E0;
        color: #A15B0E;
        border-left: 0.8rem solid #FFD362;
        i {
            color: #A15B0E80;
        }
    }

    .p-dialog-footer {
        background-color: map-get($colours, accent-purple-3);
        border-top: 1px solid map-get($colours, accent-purple-2);
        display: flex;
        justify-content: flex-end;
        align-items: center;
        gap: 1rem;
        height: 15%;
        border-bottom-right-radius: 2rem;
        border-bottom-left-radius: 2rem;
    }

    .study-assistant-modal__icon {
        color: map-get($colours, accent-purple);
        font-size: 2rem;
        height: 3rem;
        aspect-ratio: 1 / 1;
        background-color: transparent;
        display: flex;
        align-items: center;
        justify-content: center;

        &:hover {
            background-color: map-get($colours, accent-purple-2);
            cursor: pointer;
        }

        &:focus {
            outline: 1px solid map-get($colours, accent-purple-2);
            outline-offset: 1px;
        }

        &.disabled {
            cursor: not-allowed;
            
            &:hover {
                background-color: unset;
            }
        }
    }
}
</style>
